/**
 * ClockOutNoteModal — required note before an Install / Overhead / Warranty
 * stop is marked Complete. Saved by the caller to the job's Field Notes and
 * appended to today's timesheet.
 */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { SpeechToTextButton } from "@/components/SpeechToText";
import { useI18n } from "@/i18n";

export default function ClockOutNoteModal({ open, onOpenChange, jobName, kindLabel, onSubmit }) {
  const { t } = useI18n();
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setNote(""); }, [open]);

  const save = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try { await onSubmit(note.trim()); onOpenChange(false); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]" data-testid="clockout-note-modal">
        <DialogHeader>
          <DialogTitle className="text-base">{t("Clock-out note")}</DialogTitle>
          <p className="text-xs text-slate-500">{kindLabel} · {jobName}</p>
        </DialogHeader>
        <div className="space-y-2 py-1">
          <div className="flex items-center justify-between">
            <Label className="text-xs">{t("What did you do? (required)")}</Label>
            <SpeechToTextButton onTranscript={(t) => setNote(n => (n ? `${n} ${t}` : t))} />
          </div>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} autoFocus
            placeholder={t("e.g. Warranty completed — changed 1 grab bar")} data-testid="input-clockout-note" />
          <p className="text-[10px] text-slate-400">{t("Saved to this job's Field Notes and your timesheet for today.")}</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="btn-clockout-cancel">{t("Cancel")}</Button>
          <Button onClick={save} disabled={busy || !note.trim()} className="bg-green-600 hover:bg-green-700 text-white" data-testid="btn-clockout-save">
            {busy ? t("Saving…") : t("Save & Complete")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
